import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listItemGroups,
  getItemGroup,
  createItemGroup,
  updateItemGroup,
  deleteItemGroup,
  removeItemGroupMembers,
  listItemGroupSuggestions,
  resolveItemGroupSuggestions,
  resyncItemGroupRules,
  previewItemGroupRules,
  setItemGroupMemberFactor,
  recomputeItemGroupFactors,
  ItemGroup,
  ItemGroupDetail,
  ItemGroupPayload,
  ItemGroupSuggestion,
  MatchRule,
  MatchRuleKind,
  PriceStrategy,
  RulePreviewMatch,
  SizeUnit,
  VatPolicy,
} from '../lib/itemGroupApi';
import { Plus } from 'lucide-react';
import { Modal } from '../components/Modal';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import { usePagedRows } from '../hooks/usePagedRows';
import { SEGMENT_TRACK, segmentClass } from '../components/ui/tokens';
import LoadingState from '../components/LoadingState';
import { fieldErrorsOf } from '../lib/apiErrors';
import { formatDate as fmtDate, storedDay } from "../utils/dateFormat";

/**
 * Assorted-item groups — see ASSORTED_ITEM_GROUPING_PLAN.md.
 *
 * Vendors invoice USC per flavour; clients order one line. A group is the
 * overlay that collapses "YOGHURT STRAWBERRY 125G / CHERRY / MANGO" into
 * "Assorted Yoghurt" everywhere the client can see, while the member rows and
 * the stock ledger stay per-flavour underneath.
 */

const PRICE_STRATEGY_LABELS: Record<PriceStrategy, string> = {
  highest: 'Highest member price (safest)',
  latest: 'Most recently invoiced',
  weighted_avg: 'Weighted average by quantity',
  manual: 'Hand-set price',
};

const VAT_POLICY_LABELS: Record<VatPolicy, string> = {
  any_member: 'VAT if any member is VAT-applicable (safest)',
  all_members: 'VAT only if every member is VAT-applicable',
  force_on: 'Always VAT',
  force_off: 'Never VAT',
};

const RULE_KIND_LABELS: Record<MatchRuleKind, string> = {
  keyword: 'contains word',
  exact: 'is exactly',
  regex: 'matches regex',
};

/** What one group unit is called. Free text still wins; these are just the common ones. */
const COMMON_UNITS = ['PACK', 'EACH', 'CASE', 'BOX', 'BAG', 'BOTTLE', 'CAN', 'JAR', 'TUB', 'TRAY', 'KG', 'LB'];

const SIZE_UNITS: SizeUnit[] = ['g', 'kg', 'ml', 'l', 'oz', 'lb'];

/** Plain-English gloss for `groupAssignment.factorSource`. */
const FACTOR_SOURCE_LABELS: Record<string, string> = {
  same_unit: 'same unit',
  'size:uom': 'from packaging metadata',
  'size:unit_size': 'from the unit size field',
  'size:name': 'read from the item name',
  'size:unit_name': 'from the unit name',
  assumed: 'assumed — unverified',
  manual: 'set by hand',
};

const EMPTY_FORM: ItemGroupPayload = {
  name: '',
  description: '',
  isActive: true,
  uom: { level1Name: 'PACK', level2Name: '', weightUnit: null, weightPerLevel1: null },
  priceStrategy: 'highest',
  manualPrice: null,
  vatPolicy: 'any_member',
  allocationStrategy: 'fefo',
  matchRules: [{ kind: 'keyword', value: '', vendorId: null }],
  excludePatterns: [],
};

type PreviewState = {
  matches: RulePreviewMatch[];
  total: number;
  distinct: number;
  truncated: boolean;
  scanned: number;
  alreadyGrouped: number;
  locked: number;
  vetoed: number;
  vetoedRows: number;
  vetoedNames: string[];
  vendorScopedRules: number;
};

function serverMessage(err: unknown): string {
  if (!(err instanceof Error)) return 'Request failed';
  // D114.7: the message is plain text; the refused fields ride on the error (fieldErrorsOf).
  const fields = Object.values(fieldErrorsOf(err));
  return fields.length ? `${err.message}: ${fields.join('; ')}` : err.message;
}

/**
 * The server's `normalizeName`, for comparing display names on screen.
 * Two groups may share a name — only the slug is unique — so every picker has
 * to be able to tell when a name has stopped identifying one group.
 */
function normName(value?: string | null): string {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

function ttd(n?: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `TT$ ${n.toFixed(2)}`;
}

/** Group units are fractional when a member is half-sized; 30 should still read as "30". */
function qty(n?: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000);
}

const ItemGroupsPage: React.FC = () => {
  const [tab, setTab] = useState<'groups' | 'suggestions'>('groups');
  const [groups, setGroups] = useState<ItemGroup[]>([]);
  const pagedGroups = usePagedRows(groups);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [detail, setDetail] = useState<ItemGroupDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ItemGroupPayload>(EMPTY_FORM);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [factorDraft, setFactorDraft] = useState<{ id: string; value: string } | null>(null);

  const [suggestions, setSuggestions] = useState<ItemGroupSuggestion[]>([]);
  const [suggestionMeta, setSuggestionMeta] = useState({
    total: 0,
    ambiguousTotal: 0,
    page: 1,
    pageSize: 200,
    hasMore: false,
  });
  const [checkedSuggestions, setCheckedSuggestions] = useState<Set<string>>(new Set());

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listItemGroups({ includeInactive: true });
      setGroups(res.data || []);
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSuggestions = useCallback(async (page = 1) => {
    try {
      const res = await listItemGroupSuggestions(200, page);
      setSuggestions(res.data || []);
      setSuggestionMeta({
        total: res.total ?? (res.data || []).length,
        ambiguousTotal: res.ambiguousTotal ?? 0,
        page: res.page ?? page,
        pageSize: res.pageSize ?? 200,
        hasMore: !!res.hasMore,
      });
      setCheckedSuggestions(new Set());
    } catch (err) {
      setError(serverMessage(err));
    }
  }, []);

  useEffect(() => {
    loadGroups();
    loadSuggestions();
  }, [loadGroups, loadSuggestions]);

  const openDetail = async (id: string, memberPage = 1) => {
    setDetailLoading(true);
    try {
      const res = await getItemGroup(id, { memberPage });
      setDetail(res.data);
      setFactorDraft(null);
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setDetailLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, matchRules: [{ kind: 'keyword', value: '', vendorId: null }] });
    setPreview(null);
    setFormOpen(true);
  };

  const openEdit = (group: ItemGroup) => {
    setEditingId(group._id);
    setForm({
      name: group.name,
      description: group.description || '',
      isActive: group.isActive,
      category: group.category ?? null,
      sub_category: group.sub_category ?? null,
      uom: {
        level1Name: group.uom?.level1Name || 'PACK',
        level2Name: group.uom?.level2Name || '',
        weightUnit: group.uom?.weightUnit ?? null,
        weightPerLevel1: group.uom?.weightPerLevel1 ?? null,
      },
      priceStrategy: group.priceStrategy,
      manualPrice: group.manualPrice ?? null,
      vatPolicy: group.vatPolicy,
      allocationStrategy: group.allocationStrategy,
      matchRules: group.matchRules?.length ? group.matchRules : [{ kind: 'keyword', value: '', vendorId: null }],
      excludePatterns: group.excludePatterns || [],
    });
    setPreview(null);
    setFormOpen(true);
  };

  const cleanRules = useMemo(
    () => (form.matchRules || []).filter((r) => r.value.trim()),
    [form.matchRules]
  );

  /** Display names carried by more than one group — the rows a name can no longer tell apart. */
  const duplicateNames = useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    groups.forEach((g) => {
      const key = normName(g.name);
      if (!key) return;
      if (seen.has(key)) dupes.add(key);
      seen.add(key);
    });
    return dupes;
  }, [groups]);

  /**
   * The groups the name being typed would collide with. Shown while the form is
   * open rather than after saving: a duplicate name is legal, but it costs the
   * name its meaning in every picker, and that is worth knowing before the fact.
   */
  const nameTwins = useMemo(() => {
    const key = normName(form.name);
    if (!key) return [];
    return groups.filter((g) => g._id !== editingId && normName(g.name) === key);
  }, [form.name, groups, editingId]);

  const runPreview = async () => {
    if (!cleanRules.length) return;
    setPreviewing(true);
    try {
      const res = await previewItemGroupRules({
        matchRules: cleanRules,
        excludePatterns: form.excludePatterns || [],
        limit: 250,
      });
      setPreview(res.data);
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload: ItemGroupPayload = { ...form, matchRules: cleanRules };
      // `warning` is the server saying the name it just saved is now shared with
      // another group. Not an error — duplicates are allowed — but the user has
      // to hear it once, here, rather than from a suggestion that later files an
      // item into the twin.
      if (editingId) {
        const res = await updateItemGroup(editingId, payload);
        setNotice(res.warning ? `"${payload.name}" updated — ⚠ ${res.warning}` : `"${payload.name}" updated`);
      } else {
        const res = await createItemGroup(payload);
        setNotice(
          res.warning
            ? `"${payload.name}" created — ⚠ ${res.warning}`
            : `"${payload.name}" created — run "Apply rules" to sweep in items already in the catalogue`
        );
      }
      setFormOpen(false);
      await loadGroups();
      if (editingId && detail?._id === editingId) await openDetail(editingId);
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (group: ItemGroup) => {
    if (
      !window.confirm(
        `Delete "${group.name}"?\n\nIts ${group.memberCount || 0} item(s) go back to being listed individually. No catalogue or stock records are deleted.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const res = await deleteItemGroup(group._id);
      setNotice(res.message);
      if (detail?._id === group._id) setDetail(null);
      await loadGroups();
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const applyRules = async () => {
    setBusy(true);
    setError(null);
    try {
      // The rules run over the whole catalogue and the whole stock list. suggest:true
      // additionally asks the AI about what the rules missed — that pass is budgeted,
      // and the reply says how much of it was left over. Suggestions land in the
      // review tab; nothing is grouped without a human accepting it.
      const res = await resyncItemGroupRules({ suggest: true });
      setNotice(res.message);
      await Promise.all([loadGroups(), loadSuggestions(1)]);
      if (detail) await openDetail(detail._id);
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const resolveSuggestions = async (accept: boolean) => {
    const ids = Array.from(checkedSuggestions);
    if (!ids.length) return;
    setBusy(true);
    try {
      const res = await resolveItemGroupSuggestions(accept ? { accept: ids } : { reject: ids });
      setNotice(res.message);
      await Promise.all([loadGroups(), loadSuggestions(suggestionMeta.page)]);
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setBusy(false);
    }
  };

  /** Resolve the whole queue server-side, not just the page on screen. */
  const resolveAllSuggestions = async (accept: boolean) => {
    if (
      !window.confirm(
        `${accept ? 'Accept' : 'Reject'} all ${suggestionMeta.total} pending suggestion(s)?\n\n` +
          (accept
            ? 'Every one of them joins its suggested group, locked so re-ingest cannot undo it.'
            : 'Every one of them is dismissed and will not be suggested again.') +
          // This resolves rows on pages nobody has looked at, so the ambiguity
          // has to be stated here too — the badges on screen cannot speak for them.
          (accept && suggestionMeta.ambiguousTotal
            ? `\n\n⚠ ${suggestionMeta.ambiguousTotal} of them target a group whose name is shared with another group. ` +
              'Accepting in bulk files those items without anyone having confirmed which of the same-named groups was meant.'
            : '')
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const res = await resolveItemGroupSuggestions({ all: accept ? 'accept' : 'reject' });
      setNotice(res.message);
      await Promise.all([loadGroups(), loadSuggestions(1)]);
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const dropMember = async (memberId: string) => {
    if (!detail) return;
    setBusy(true);
    try {
      await removeItemGroupMembers(detail._id, [memberId]);
      await Promise.all([openDetail(detail._id, detail.memberPage || 1), loadGroups()]);
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Correct one member's conversion. An empty box means "go back to whatever the
   * unit and size imply" rather than "1", so a bad override is undoable.
   */
  const saveFactor = async (memberId: string, raw: string) => {
    if (!detail) return;
    const trimmed = raw.trim();
    const value = trimmed === '' ? null : Number(trimmed);
    if (value != null && (!Number.isFinite(value) || value <= 0)) {
      setError('A factor has to be a number above 0 — leave it blank to use the derived value.');
      return;
    }
    setBusy(true);
    try {
      const res = await setItemGroupMemberFactor(detail._id, memberId, value);
      setNotice(res.message);
      setFactorDraft(null);
      await openDetail(detail._id, detail.memberPage || 1);
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const rederiveFactors = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      const res = await recomputeItemGroupFactors(detail._id);
      setNotice(res.message);
      await openDetail(detail._id, detail.memberPage || 1);
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const totalSuggestions = suggestionMeta.total;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="border-b border-slate-200 bg-brand-bg">
        <div className="flex flex-wrap items-center gap-3 px-4 pb-3 pt-4 sm:px-8">
          <div className="mr-auto">
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Assorted item groups</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Vendors invoice by flavour; clients order one line. A group collapses the variants into
              a single client-facing item without losing which supplier each one came from.
            </p>
          </div>
          <Button
            onClick={applyRules}
            disabled={busy || !groups.length}
            title="Re-run every group's rules over every catalogue and stock row that already exists — the whole catalogue, not a first page of it"
          >
            {busy ? 'Working…' : 'Apply rules to existing items'}
          </Button>
          <Button variant="primary" onClick={openCreate} icon={<Plus aria-hidden="true" className="h-4 w-4" />}>
            New group
          </Button>
        </div>

        <div className="px-6 py-2 border-t border-[#D7DBD9]/50">
          <div className={SEGMENT_TRACK}>
          {(['groups', 'suggestions'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={segmentClass(tab === t)}
            >
              {t === 'groups' ? `Groups (${groups.length})` : `Needs review (${totalSuggestions})`}
            </button>
          ))}
          </div>
        </div>
      </div>

      {(error || notice) && (
        <div className="px-6 pt-4">
          <div
            className={`rounded-md border px-3 py-2 text-xs flex items-start gap-2 ${
              error ? 'border-red-200 bg-red-50 text-red-800' : 'border-green-200 bg-green-50 text-green-800'
            }`}
          >
            <span className="flex-1">{error || notice}</span>
            <button
              type="button"
              className="font-semibold"
              onClick={() => {
                setError(null);
                setNotice(null);
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {tab === 'groups' && (
        <div className="px-6 py-4">
          {loading ? (
            <LoadingState />
          ) : !groups.length ? (
            <div className="border border-dashed border-[#D7DBD9] rounded-md p-10 text-center">
              <p className="text-[13px] text-slate-900 font-medium">No item groups yet</p>
              <p className="text-xs text-slate-500 mt-1 max-w-lg mx-auto">
                Create one for each assorted product — "Assorted Yoghurt", "Assorted Cookies" — and give
                it a keyword rule. Every vendor invoice uploaded from then on files its flavour variants
                into the group automatically.
              </p>
            </div>
          ) : (
            <div className="border border-[#D7DBD9] rounded-md overflow-auto bg-white">
              {/* The group's name stays put while the counts scroll
                  (`frozen-cols-*`, index.css). */}
              <table
                className="w-full border-collapse min-w-[900px] frozen-cols-1"
                style={{ '--frozen-divider': '#D7DBD9' } as React.CSSProperties}
              >
                <thead>
                  <tr className="bg-[#F9FAF9] text-left text-xs font-semibold text-slate-600">
                    <th className="py-2 px-3 border-b border-[#D7DBD9] bg-[#F9FAF9]">Group</th>
                    <th className="py-2 px-3 border-b border-[#D7DBD9] w-[150px]">Rules</th>
                    <th className="py-2 px-3 border-b border-[#D7DBD9] w-[90px]">Unit</th>
                    <th className="py-2 px-3 border-b border-[#D7DBD9] w-[150px]">Price strategy</th>
                    <th className="py-2 px-3 border-b border-[#D7DBD9] w-[90px] text-right">Catalogue</th>
                    <th className="py-2 px-3 border-b border-[#D7DBD9] w-[90px] text-right">Stock rows</th>
                    <th className="py-2 px-3 border-b border-[#D7DBD9] w-[110px] text-right">To review</th>
                    <th className="py-2 px-3 border-b border-[#D7DBD9] w-[150px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedGroups.visible.map((g) => (
                    <tr key={g._id} className="text-xs text-slate-900 bg-white hover:bg-[#F9FAF9]">
                      <td className="py-2 px-3 border-b border-[#D7DBD9] bg-inherit">
                        <button type="button" className="font-medium hover:underline" onClick={() => openDetail(g._id)}>
                          {g.name}
                        </button>
                        {/* Two groups may share a name; the slug is the only thing
                            that can tell them apart, so show it exactly when the
                            name has stopped doing that job. */}
                        {duplicateNames.has(normName(g.name)) && (
                          <span
                            className="ml-2 text-xs font-mono font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800"
                            title={`Another group is also called "${g.name}". This one is "${g.slug}" — the name alone will not tell them apart in the GROUP picker or the review queue.`}
                          >
                            ⚠ {g.slug}
                          </span>
                        )}
                        {!g.isActive && (
                          <span className="ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            inactive
                          </span>
                        )}
                        {g.description && <div className="text-xs text-slate-500 mt-0.5">{g.description}</div>}
                      </td>
                      <td className="py-2 px-3 border-b border-[#D7DBD9] text-xs text-slate-600">
                        {g.matchRules?.length
                          ? g.matchRules.map((r) => r.value).join(', ')
                          : <span className="text-slate-400">manual only</span>}
                      </td>
                      <td className="py-2 px-3 border-b border-[#D7DBD9]">
                        {g.uom?.level1Name || '—'}
                        {g.uom?.weightPerLevel1 && g.uom?.weightUnit ? (
                          <div className="text-xs text-slate-500">
                            {g.uom.weightPerLevel1}
                            {g.uom.weightUnit} each
                          </div>
                        ) : (
                          <div className="text-xs text-slate-400">no size set</div>
                        )}
                      </td>
                      <td className="py-2 px-3 border-b border-[#D7DBD9] text-xs">
                        {g.priceStrategy === 'manual' ? ttd(g.manualPrice) : PRICE_STRATEGY_LABELS[g.priceStrategy]}
                      </td>
                      <td className="py-2 px-3 border-b border-[#D7DBD9] text-right">{g.memberCount ?? 0}</td>
                      <td className="py-2 px-3 border-b border-[#D7DBD9] text-right">{g.inventoryMemberCount ?? 0}</td>
                      <td className="py-2 px-3 border-b border-[#D7DBD9] text-right">
                        {g.suggestionCount ? (
                          <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold px-1.5 py-0.5">
                            {g.suggestionCount}
                          </span>
                        ) : (
                          <span className="text-[#CCC]">—</span>
                        )}
                      </td>
                      <td className="py-2 px-3 border-b border-[#D7DBD9] text-right whitespace-nowrap">
                        <button type="button" className="text-blue-700 hover:underline mr-3" onClick={() => openEdit(g)}>
                          Edit
                        </button>
                        <button type="button" className="text-red-600 hover:underline" onClick={() => remove(g)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination page={pagedGroups.page} pageSize={pagedGroups.pageSize} total={pagedGroups.total} onPageChange={pagedGroups.setPage} noun="groups" />
        </div>
      )}

      {tab === 'suggestions' && (
        <div className="px-6 py-4">
          <div className="mb-3 text-xs text-slate-600">
            Items the AI thinks belong to a group but no rule caught. Nothing here affects a price list
            until you accept it. Rejecting also stops the same suggestion coming back next month.
          </div>
          {/* Accepting files an item into a specific group id. When two groups
              share a display name, the row alone cannot say which — so say it
              here, before anyone reaches for "Accept all". */}
          {suggestionMeta.ambiguousTotal > 0 && (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <strong>{suggestionMeta.ambiguousTotal} of {suggestionMeta.total} pending suggestion(s)</strong> target a
              group whose name is shared with another group. The slug under each name is the one that identifies it —
              check it before accepting, or rename one of the groups so the name means one thing again.
            </div>
          )}
          {!suggestions.length ? (
            <div className="border border-dashed border-[#D7DBD9] rounded-md p-10 text-center text-xs text-slate-500">
              Nothing to review.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <button
                  type="button"
                  disabled={busy || !checkedSuggestions.size}
                  onClick={() => resolveSuggestions(true)}
                  className="h-8 px-3 rounded-md text-xs font-semibold bg-primary-500 text-white disabled:opacity-50"
                >
                  Accept {checkedSuggestions.size || ''}
                </button>
                <button
                  type="button"
                  disabled={busy || !checkedSuggestions.size}
                  onClick={() => resolveSuggestions(false)}
                  className="h-8 px-3 rounded-md text-xs font-semibold border border-[#D7DBD9] text-slate-900 disabled:opacity-50"
                >
                  Reject {checkedSuggestions.size || ''}
                </button>

                {suggestionMeta.total > suggestions.length && (
                  <>
                    <span className="text-xs text-slate-500 mx-1">|</span>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => resolveAllSuggestions(true)}
                      className="h-8 px-3 rounded-md text-xs font-semibold border border-blue-700 text-blue-800 bg-blue-50 disabled:opacity-50"
                      title="Accept every pending suggestion, not just this page"
                    >
                      Accept all {suggestionMeta.total}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => resolveAllSuggestions(false)}
                      className="h-8 px-3 rounded-md text-xs font-semibold border border-[#D7DBD9] text-slate-900 disabled:opacity-50"
                      title="Reject every pending suggestion, not just this page"
                    >
                      Reject all
                    </button>
                  </>
                )}

                <div className="ml-auto flex items-center gap-2 text-xs text-slate-600">
                  <button
                    type="button"
                    disabled={busy || suggestionMeta.page <= 1}
                    onClick={() => loadSuggestions(suggestionMeta.page - 1)}
                    className="h-7 px-2 rounded border border-[#D7DBD9] bg-white disabled:opacity-40"
                  >
                    ←
                  </button>
                  <span>
                    {(suggestionMeta.page - 1) * suggestionMeta.pageSize + 1}–
                    {(suggestionMeta.page - 1) * suggestionMeta.pageSize + suggestions.length} of{' '}
                    {suggestionMeta.total}
                  </span>
                  <button
                    type="button"
                    disabled={busy || !suggestionMeta.hasMore}
                    onClick={() => loadSuggestions(suggestionMeta.page + 1)}
                    className="h-7 px-2 rounded border border-[#D7DBD9] bg-white disabled:opacity-40"
                  >
                    →
                  </button>
                </div>
              </div>
              <div className="border border-[#D7DBD9] rounded-md overflow-auto bg-white">
                {/* Tick box and item name stay put while supplier, suggestion
                    and confidence scroll (`frozen-cols-*`, index.css). 40px is
                    the first column's own `w-10`. */}
                <table
                  className="w-full border-collapse min-w-[800px] frozen-cols-2"
                  style={{ '--frozen-col-2': '40px', '--frozen-divider': '#D7DBD9' } as React.CSSProperties}
                >
                  <thead>
                    <tr className="bg-[#F9FAF9] text-left text-xs font-semibold text-slate-600">
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-10 bg-[#F9FAF9]">
                        <input
                          type="checkbox"
                          checked={checkedSuggestions.size === suggestions.length && suggestions.length > 0}
                          onChange={(e) =>
                            setCheckedSuggestions(e.target.checked ? new Set(suggestions.map((s) => s._id)) : new Set())
                          }
                        />
                      </th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] bg-[#F9FAF9]">Catalogue item</th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-[180px]">Supplier</th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-[200px]">Suggested group</th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-[110px] text-right">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestions.map((s) => (
                      <tr key={s._id} className="text-xs text-slate-900 bg-white hover:bg-[#F9FAF9]">
                        <td className="py-2 px-3 border-b border-[#D7DBD9] bg-inherit">
                          <input
                            type="checkbox"
                            checked={checkedSuggestions.has(s._id)}
                            onChange={() => {
                              const next = new Set(checkedSuggestions);
                              if (next.has(s._id)) next.delete(s._id);
                              else next.add(s._id);
                              setCheckedSuggestions(next);
                            }}
                          />
                        </td>
                        <td className="py-2 px-3 border-b border-[#D7DBD9] bg-inherit">{s.item_name}</td>
                        <td className="py-2 px-3 border-b border-[#D7DBD9] text-xs text-slate-600">
                          {s.supplier || '—'}
                        </td>
                        {/* The suggestion has always stored an unambiguous group
                            id; the screen showed only the name, which two groups
                            may share. Name, then what separates it from its twin:
                            the slug, and how many items are already in it. */}
                        <td className="py-2 px-3 border-b border-[#D7DBD9]">
                          <div className="flex items-center gap-1.5">
                            <span>{s.suggestedGroupName}</span>
                            {s.suggestedGroupIsAmbiguous && (
                              <span
                                className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800"
                                title={`${s.sameNameGroups?.length ?? 2} groups are called "${s.suggestedGroupName}". This item is going into ${
                                  s.suggestedGroupSlug
                                }.\n\n${(s.sameNameGroups || [])
                                  .map(
                                    (t) =>
                                      `${t.isTarget ? '→ ' : '   '}${t.slug} — ${t.memberCount} member(s), ${t.ruleCount} rule(s)${
                                        t.isActive ? '' : ', inactive'
                                      }`
                                  )
                                  .join('\n')}\n\nIf that is the wrong one, reject this and use the GROUP button on the Master Catalogue to pick the group by slug.`}
                              >
                                ⚠ duplicate name
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5 font-mono">
                            {s.suggestedGroupSlug || '—'}
                            {s.suggestedGroupMemberCount != null && (
                              <span className="font-sans">
                                {' · '}
                                {s.suggestedGroupMemberCount} member
                                {s.suggestedGroupMemberCount === 1 ? '' : 's'}
                              </span>
                            )}
                            {s.suggestedGroupRules?.length ? (
                              <span className="font-sans"> · {s.suggestedGroupRules.join(', ')}</span>
                            ) : (
                              <span className="font-sans"> · no rules</span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 border-b border-[#D7DBD9] text-right">
                          {s.confidence == null ? '—' : `${Math.round(s.confidence * 100)}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Create / edit ────────────────────────────────────────────── */}
      <Modal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        title={editingId ? 'Edit item group' : 'New item group'}
        maxWidthClass="max-w-3xl"
      >
        <div className="p-5 space-y-5 text-xs">
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="font-semibold text-slate-900">Group name (what the client sees)</span>
              <input
                value={form.name || ''}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Assorted Yoghurt"
                className="mt-1 w-full px-3 py-2 border border-[#D7DBD9] rounded-md"
              />
              {/* Not blocking — a duplicate name saves cleanly and always has.
                  But from then on every screen that picks a group by name shows
                  two identical options, so it is said here rather than
                  discovered later from an item filed into the wrong one. */}
              {nameTwins.length > 0 && (
                <span className="mt-1 block text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  ⚠ {nameTwins.length === 1 ? 'A group is' : `${nameTwins.length} groups are`} already called "
                  {form.name}" ({nameTwins.map((t) => t.slug).join(', ')}
                  ). They will be indistinguishable in the GROUP picker and the AI review queue — consider a more
                  specific name.
                </span>
              )}
            </label>
            <label className="block">
              <span className="font-semibold text-slate-900">The client orders this group in…</span>
              <input
                list="group-unit-options"
                value={form.uom?.level1Name || ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, uom: { ...(f.uom || { level1Name: 'PACK' }), level1Name: e.target.value } }))
                }
                placeholder="PACK"
                className="mt-1 w-full px-3 py-2 border border-[#D7DBD9] rounded-md uppercase"
              />
              <datalist id="group-unit-options">
                {COMMON_UNITS.map((u) => (
                  <option key={u} value={u} />
                ))}
              </datalist>
              <span className="text-xs text-slate-500">
                One line on the price list, quoted and counted in this unit. Spelling doesn't matter —
                PACK, PACKS and PKT are treated as the same unit.
              </span>
            </label>
          </div>

          {/* The one field that decides whether "10 + 10 + 5 = 30" is true. */}
          <div className="grid grid-cols-2 gap-4 border border-[#D7DBD9] rounded-md p-3 bg-[#F9FAF9]">
            <label className="block">
              <span className="font-semibold text-slate-900">
                Size of one {(form.uom?.level1Name || 'PACK').toUpperCase() || 'PACK'}
              </span>
              <div className="flex gap-2 mt-1">
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={form.uom?.weightPerLevel1 ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      uom: {
                        ...(f.uom || { level1Name: 'PACK' }),
                        weightPerLevel1: e.target.value === '' ? null : Number(e.target.value),
                      },
                    }))
                  }
                  placeholder="125"
                  className="flex-1 px-3 py-2 border border-[#D7DBD9] rounded-md"
                />
                <select
                  value={form.uom?.weightUnit || ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      uom: {
                        ...(f.uom || { level1Name: 'PACK' }),
                        weightUnit: (e.target.value || null) as SizeUnit | null,
                      },
                    }))
                  }
                  className="w-24 px-2 py-2 border border-[#D7DBD9] rounded-md bg-white"
                >
                  <option value="">—</option>
                  {SIZE_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            </label>

            <div className="text-xs text-slate-600 self-center">
              {form.uom?.weightPerLevel1 && form.uom?.weightUnit ? (
                <>
                  <span className="font-semibold text-slate-900">
                    1 {(form.uom.level1Name || 'PACK').toUpperCase()} = {form.uom.weightPerLevel1}{' '}
                    {form.uom.weightUnit}
                  </span>
                  <div className="mt-1">
                    Every member is converted against this, so a{' '}
                    {form.uom.weightPerLevel1 * 2}
                    {form.uom.weightUnit} pack counts as 2 and a{' '}
                    {form.uom.weightPerLevel1 / 2}
                    {form.uom.weightUnit} pack as 0.5. Members in lb or oz convert automatically.
                  </div>
                </>
              ) : (
                <>
                  <span className="font-semibold text-slate-900">Optional, but it is what makes totals honest.</span>
                  <div className="mt-1">
                    Without a size, a 125g pot and a 500g tub both count as one{' '}
                    {(form.uom?.level1Name || 'PACK').toUpperCase()}. With one, USC reads each member's own size —
                    from its packaging data, its unit size, or the size printed in the vendor's description — and
                    converts it.
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="font-semibold text-slate-900">Price the group quotes</span>
              <select
                value={form.priceStrategy}
                onChange={(e) => setForm((f) => ({ ...f, priceStrategy: e.target.value as PriceStrategy }))}
                className="mt-1 w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white"
              >
                {(Object.keys(PRICE_STRATEGY_LABELS) as PriceStrategy[]).map((k) => (
                  <option key={k} value={k}>
                    {PRICE_STRATEGY_LABELS[k]}
                  </option>
                ))}
              </select>
              {form.priceStrategy === 'manual' && (
                <input
                  type="number"
                  step="0.01"
                  value={form.manualPrice ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, manualPrice: e.target.value === '' ? null : Number(e.target.value) }))
                  }
                  placeholder="Price in TT$"
                  className="mt-2 w-full px-3 py-2 border border-[#D7DBD9] rounded-md"
                />
              )}
            </label>

            <label className="block">
              <span className="font-semibold text-slate-900">VAT</span>
              <select
                value={form.vatPolicy}
                onChange={(e) => setForm((f) => ({ ...f, vatPolicy: e.target.value as VatPolicy }))}
                className="mt-1 w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white"
              >
                {(Object.keys(VAT_POLICY_LABELS) as VatPolicy[]).map((k) => (
                  <option key={k} value={k}>
                    {VAT_POLICY_LABELS[k]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* Rules */}
          <div>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-900">
                Match rules — how invoice lines join this group
              </span>
              <button
                type="button"
                className="text-blue-700 text-xs font-semibold hover:underline"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    matchRules: [...(f.matchRules || []), { kind: 'keyword', value: '', vendorId: null }],
                  }))
                }
              >
                + add rule
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {(form.matchRules || []).map((rule: MatchRule, i: number) => (
                <div key={i} className="flex gap-2 items-center">
                  <select
                    value={rule.kind}
                    onChange={(e) =>
                      setForm((f) => {
                        const rules = [...(f.matchRules || [])];
                        rules[i] = { ...rules[i], kind: e.target.value as MatchRuleKind };
                        return { ...f, matchRules: rules };
                      })
                    }
                    className="w-40 px-2 py-2 border border-[#D7DBD9] rounded-md bg-white"
                  >
                    {(Object.keys(RULE_KIND_LABELS) as MatchRuleKind[]).map((k) => (
                      <option key={k} value={k}>
                        {RULE_KIND_LABELS[k]}
                      </option>
                    ))}
                  </select>
                  <input
                    value={rule.value}
                    onChange={(e) =>
                      setForm((f) => {
                        const rules = [...(f.matchRules || [])];
                        rules[i] = { ...rules[i], value: e.target.value };
                        return { ...f, matchRules: rules };
                      })
                    }
                    placeholder="yoghurt"
                    className="flex-1 px-3 py-2 border border-[#D7DBD9] rounded-md"
                  />
                  <button
                    type="button"
                    className="text-red-600 text-xs px-2"
                    onClick={() =>
                      setForm((f) => ({ ...f, matchRules: (f.matchRules || []).filter((_, j) => j !== i) }))
                    }
                  >
                    remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="font-semibold text-slate-900">Never include (comma separated)</span>
            <input
              value={(form.excludePatterns || []).join(', ')}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  excludePatterns: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                }))
              }
              placeholder="DRINK, FROZEN"
              className="mt-1 w-full px-3 py-2 border border-[#D7DBD9] rounded-md"
            />
            <span className="text-xs text-slate-500">
              A veto that beats every rule — this is what keeps "YOGHURT DRINK" out of "Assorted Yoghurt".
            </span>
          </label>

          {/* Dry run — grouping changes what a client is quoted, so show the damage first. */}
          <div className="border border-[#D7DBD9] rounded-md p-3 bg-[#F9FAF9]">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-900">Test the rules against the catalogue</span>
              <button
                type="button"
                onClick={runPreview}
                disabled={previewing || !cleanRules.length}
                className="h-7 px-3 rounded-md text-xs font-semibold border border-blue-700 text-blue-800 bg-white disabled:opacity-50"
              >
                {previewing ? 'Checking…' : 'Preview matches'}
              </button>
            </div>
            {preview && (
              <div className="mt-2 text-xs text-slate-700">
                <div className="font-semibold mb-1 text-slate-900">
                  {preview.distinct} product(s) would join, across {preview.total} catalogue row(s)
                </div>
                <div className="text-slate-500 mb-2">
                  Checked all {preview.scanned} rows in the catalogue.
                  {preview.alreadyGrouped > 0 && ` ${preview.alreadyGrouped} already belong to a group.`}
                  {preview.locked > 0 &&
                    ` ${preview.locked} were set by hand and will keep whatever a person chose.`}
                  {preview.vendorScopedRules > 0 &&
                    ' Vendor-scoped rules can only be checked at invoice upload, so this count may be conservative.'}
                </div>

                {preview.vetoed > 0 && (
                  <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-2 py-1.5">
                    <span className="font-semibold">
                      {preview.vetoed} product(s) matched a rule but were blocked by "Never include"
                    </span>
                    <div className="mt-0.5">
                      {preview.vetoedNames.join(', ')}
                      {preview.vetoed > preview.vetoedNames.length ? ', …' : ''}
                    </div>
                  </div>
                )}

                <div className="max-h-56 overflow-auto border border-[#D7DBD9] rounded-md bg-white">
                  <table className="w-full border-collapse">
                    <tbody>
                      {preview.matches.map((m) => (
                        <tr key={m._id} className="border-b border-[#D7DBD9] last:border-0">
                          <td className="py-1 px-2">
                            {m.item_name}
                            {m.alreadyGrouped && (
                              <span className="ml-2 text-xs px-1 rounded bg-blue-50 border border-blue-200 text-blue-800">
                                already grouped
                              </span>
                            )}
                            {m.locked && (
                              <span className="ml-2 text-xs px-1 rounded bg-gray-100 border border-gray-200 text-gray-700">
                                set by hand
                              </span>
                            )}
                          </td>
                          <td className="py-1 px-2 w-[110px] text-right text-slate-500">
                            {m.rowCount} row{m.rowCount === 1 ? '' : 's'}
                          </td>
                        </tr>
                      ))}
                      {!preview.matches.length && (
                        <tr>
                          <td className="py-3 px-2 text-center text-slate-500">No matches.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {preview.truncated && (
                  <div className="mt-1 text-slate-500">
                    Showing the {preview.matches.length} most-invoiced of {preview.distinct} — all{' '}
                    {preview.distinct} will be grouped.
                  </div>
                )}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.isActive !== false}
              onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            />
            <span>Active — inactive groups stop claiming new invoice lines</span>
          </label>
        </div>

        <div className="px-5 py-3 border-t border-[#D7DBD9] flex justify-end gap-2 bg-[#F9FAF9]">
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={busy || !form.name?.trim()}>
            {busy ? 'Saving…' : editingId ? 'Save' : 'Create'}
          </Button>
        </div>
      </Modal>

      {/* ── Detail ───────────────────────────────────────────────────── */}
      <Modal
        isOpen={!!detail}
        onClose={() => setDetail(null)}
        title={detail?.name || 'Item group'}
        maxWidthClass="max-w-5xl"
      >
        {detailLoading || !detail ? (
          <div className="p-8">
            <LoadingState />
          </div>
        ) : (
          <div className="p-5 space-y-5 text-xs">
            <div className="grid grid-cols-4 gap-3">
              <div className="border border-[#D7DBD9] rounded-md p-3">
                <div className="text-xs text-slate-500">Group price</div>
                <div className="text-base font-semibold">{ttd(detail.rollup.price)}</div>
                <div className="text-xs text-slate-500">
                  per {detail.rollup.uomLevel1Name} · {PRICE_STRATEGY_LABELS[detail.priceStrategy]}
                </div>
              </div>
              <div className="border border-[#D7DBD9] rounded-md p-3">
                <div className="text-xs text-slate-500">Member spread</div>
                <div className="text-base font-semibold">
                  {detail.rollup.priceSpread
                    ? `${ttd(detail.rollup.priceSpread.min)} – ${ttd(detail.rollup.priceSpread.max)}`
                    : '—'}
                </div>
                <div className="text-xs text-slate-500">internal only — the client sees one price</div>
              </div>
              <div className="border border-[#D7DBD9] rounded-md p-3">
                <div className="text-xs text-slate-500">Stock across members</div>
                <div className="text-base font-semibold">
                  {qty(detail.rollup.stockLevel1)} {detail.rollup.uomLevel1Name}
                </div>
                <div className="text-xs text-slate-500">
                  {detail.inventoryMemberCount ?? 0} stock record(s)
                </div>
              </div>
              <div className="border border-[#D7DBD9] rounded-md p-3">
                <div className="text-xs text-slate-500">VAT</div>
                <div className="text-base font-semibold">{detail.rollup.vat ? 'Applicable' : 'Not applicable'}</div>
                {detail.rollup.vatMixed && (
                  <div className="text-xs text-amber-700 font-medium">members disagree — check this</div>
                )}
              </div>
            </div>

            {!!detail.warningCount && (
              <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-xs flex items-center gap-3">
                <span className="flex-1">
                  <span className="font-semibold">
                    {detail.warningCount} member(s) carry a unit warning.
                  </span>{' '}
                  Their conversion to {detail.rollup.uomLevel1Name} was inferred rather than read from
                  packaging data, so the stock total above is only as good as that guess. Set the factor by
                  hand on any row you don't trust.
                </span>
                <button
                  type="button"
                  onClick={rederiveFactors}
                  disabled={busy}
                  className="shrink-0 h-7 px-2 rounded-md text-xs font-semibold border border-amber-300 bg-white text-amber-900 disabled:opacity-50"
                  title="Re-derive every factor from the group's current unit and the members' latest metadata"
                >
                  Re-derive
                </button>
              </div>
            )}

            <div>
              <div className="flex items-baseline justify-between mb-2">
                <div className="font-semibold">Catalogue members ({detail.memberCount})</div>
                <div className="text-xs text-slate-500">
                  1 member unit = <span className="font-semibold">factor</span> ×{' '}
                  {detail.rollup.uomLevel1Name}. Click a factor to correct it.
                </div>
              </div>
              <div className="border border-[#D7DBD9] rounded-md overflow-auto max-h-72">
                {/* The vendor's description stays put while the unit and
                    conversion columns scroll (`frozen-cols-*`, index.css). */}
                <table
                  className="w-full border-collapse min-w-[860px] frozen-cols-1"
                  style={{ '--frozen-divider': '#D7DBD9' } as React.CSSProperties}
                >
                  <thead>
                    <tr className="bg-[#F9FAF9] text-left text-xs font-semibold text-slate-600 sticky top-0">
                      <th className="py-2 px-3 border-b border-[#D7DBD9] bg-[#F9FAF9]">Vendor's description</th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-[150px]">Supplier</th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-[90px]">Its unit</th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-[190px] text-right">Counts as</th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-[110px] text-right">Per group unit</th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-[90px]">Added by</th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-[70px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.members.map((m) => (
                      <tr key={m._id} className="text-xs bg-white hover:bg-[#F9FAF9] align-top">
                        <td className="py-2 px-3 border-b border-[#D7DBD9] bg-inherit">
                          {m.item_name}
                          {m.setsGroupPrice && (
                            <span className="ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800">
                              sets the price
                            </span>
                          )}
                          {m.uomWarning && (
                            <div className="text-xs text-amber-700 mt-0.5">⚠ {m.uomWarning}</div>
                          )}
                        </td>
                        <td className="py-2 px-3 border-b border-[#D7DBD9] text-xs text-slate-600">
                          {m.supplier || '—'}
                        </td>
                        <td className="py-2 px-3 border-b border-[#D7DBD9] text-xs text-slate-600">
                          {m.uomLevel1Name || '—'}
                          {m.unitSize && <div className="text-xs text-slate-500">{m.unitSize}</div>}
                        </td>
                        <td className="py-2 px-3 border-b border-[#D7DBD9] text-right">
                          {factorDraft?.id === m._id ? (
                            <div className="flex items-center justify-end gap-1">
                              <input
                                autoFocus
                                type="number"
                                step="any"
                                min="0"
                                value={factorDraft.value}
                                placeholder="auto"
                                onChange={(e) => setFactorDraft({ id: m._id, value: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveFactor(m._id, factorDraft.value);
                                  if (e.key === 'Escape') setFactorDraft(null);
                                }}
                                className="w-20 px-2 py-1 border border-blue-400 rounded text-right"
                              />
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => saveFactor(m._id, factorDraft.value)}
                                className="text-xs font-semibold text-blue-700 disabled:opacity-50"
                              >
                                save
                              </button>
                              <button
                                type="button"
                                onClick={() => setFactorDraft(null)}
                                className="text-xs text-slate-500"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setFactorDraft({ id: m._id, value: String(m.factor ?? 1) })}
                              className="text-right hover:underline"
                              title={m.factorNote || 'Set this member’s conversion by hand'}
                            >
                              <span className="font-medium">
                                {qty(m.factor)} {detail.rollup.uomLevel1Name}
                              </span>
                              <div className="text-xs text-slate-500">
                                {FACTOR_SOURCE_LABELS[m.factorSource || ''] || 'not derived'}
                              </div>
                            </button>
                          )}
                        </td>
                        <td className="py-2 px-3 border-b border-[#D7DBD9] text-right">
                          {ttd(m.priceInGroupUnits)}
                        </td>
                        <td className="py-2 px-3 border-b border-[#D7DBD9] text-xs">{m.method || '—'}</td>
                        <td className="py-2 px-3 border-b border-[#D7DBD9] text-right">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => dropMember(m._id)}
                            className="text-red-600 text-xs hover:underline disabled:opacity-50"
                            title="Remove from this group and never re-suggest it"
                          >
                            remove
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!detail.members.length && (
                      <tr>
                        <td colSpan={7} className="py-6 text-center text-slate-500">
                          No members yet. Add a rule, or select items on the Master Catalogue and use "GROUP".
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {(detail.truncated || (detail.memberPage || 1) > 1) && (
                <div className="flex items-center gap-2 mt-2 text-xs text-slate-600">
                  <button
                    type="button"
                    disabled={detailLoading || (detail.memberPage || 1) <= 1}
                    onClick={() => openDetail(detail._id, (detail.memberPage || 1) - 1)}
                    className="h-7 px-2 rounded border border-[#D7DBD9] bg-white disabled:opacity-40"
                  >
                    ← Previous
                  </button>
                  <span>
                    Members {(((detail.memberPage || 1) - 1) * (detail.memberPageSize || 100)) + 1}–
                    {((detail.memberPage || 1) - 1) * (detail.memberPageSize || 100) + detail.members.length} of{' '}
                    {detail.memberCount}
                  </span>
                  <button
                    type="button"
                    disabled={detailLoading || !detail.truncated}
                    onClick={() => openDetail(detail._id, (detail.memberPage || 1) + 1)}
                    className="h-7 px-2 rounded border border-[#D7DBD9] bg-white disabled:opacity-40"
                  >
                    Next →
                  </button>
                  <span className="text-slate-500">
                    The price and stock figures above cover every member, not this page.
                  </span>
                </div>
              )}
            </div>

            <div>
              <div className="font-semibold mb-2">
                Stock behind the group ({detail.inventoryMemberCount ?? detail.inventoryMembers.length})
              </div>
              <div className="border border-[#D7DBD9] rounded-md overflow-auto max-h-60">
                <table className="w-full border-collapse min-w-[700px]">
                  <thead>
                    <tr className="bg-[#F9FAF9] text-left text-xs font-semibold text-slate-600 sticky top-0">
                      <th className="py-2 px-3 border-b border-[#D7DBD9]">Item</th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-[90px] text-right">On hand</th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-[110px] text-right">Group units</th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-[110px]">Expiry</th>
                      <th className="py-2 px-3 border-b border-[#D7DBD9] w-[160px]">Last supplier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.inventoryMembers.map((m) => (
                      <tr key={m._id} className="text-xs hover:bg-[#F9FAF9]">
                        <td className="py-2 px-3 border-b border-[#D7DBD9]">
                          {m.name}
                          {m.uomWarning && (
                            <div className="text-xs text-amber-700 mt-0.5">⚠ {m.uomWarning}</div>
                          )}
                        </td>
                        <td className="py-2 px-3 border-b border-[#D7DBD9] text-right">
                          {qty(m.level1Qty)} {m.uomLevel1Name}
                        </td>
                        <td
                          className="py-2 px-3 border-b border-[#D7DBD9] text-right font-medium"
                          title={m.factorNote || ''}
                        >
                          {qty(m.groupUnits)}
                          {m.factor !== 1 && (
                            <span className="text-xs text-slate-500"> (×{qty(m.factor)})</span>
                          )}
                        </td>
                        <td className="py-2 px-3 border-b border-[#D7DBD9] text-xs">
                          {fmtDate(storedDay(m.expiryDate), '—')}
                        </td>
                        <td className="py-2 px-3 border-b border-[#D7DBD9] text-xs text-slate-600">
                          {m.lastSupplierName || '—'}
                        </td>
                      </tr>
                    ))}
                    {!detail.inventoryMembers.length && (
                      <tr>
                        <td colSpan={5} className="py-6 text-center text-slate-500">
                          No stock records linked yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Stock, cost and expiry stay per-flavour on purpose — that is what keeps "which supplier did
                this come from" answerable after the group collapses the line.
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ItemGroupsPage;
